import {
  Anchor,
  Box,
  Breadcrumbs,
  Button,
  Container,
  Group,
  Paper,
  SimpleGrid,
  Stack,
  Text,
  ThemeIcon,
  Title,
} from '@mantine/core'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import { useState } from 'react'
import { FaWhatsapp } from 'react-icons/fa'
import { LuArrowRight, LuFlaskConical, LuMail, LuMapPin, LuMessageCircle } from 'react-icons/lu'
import classes from './contact.module.css'

export const Route = createFileRoute('/contact')({
  component: ContactPage,
})

// Indonesian B2B sales is WhatsApp-first. The previous 6-field "lead form"
// (name + email + phone + marina + berth count + message) felt like a credit
// application — most visitors bounced rather than fill it. New layout:
//   1. Big WhatsApp button — the actual fastest path
//   2. Demo CTA — "see it before you talk to us"
//   3. Email + office address — for the few who prefer async
// Form removed entirely; backend lead capture was a stub anyway.

const WA_NUMBER = '6281234567890'
const WA_LINK = `https://wa.me/${WA_NUMBER}?text=${encodeURIComponent(
  'Halo, saya tertarik dengan JP MARINE dan ingin tanya-tanya soal demo / pricing.',
)}`

function ContactPage() {
  return (
    <Box className={classes.page}>
      <Container size="lg" py="xl">
        <Breadcrumbs mb="lg">
          <Anchor component={Link} to="/" c="dimmed">
            Home
          </Anchor>
          <span>Kontak</span>
        </Breadcrumbs>
        <Stack gap="xs" mb="xl">
          <Title order={1}>Hubungi Kami</Title>
          <Text c="dimmed" size="lg" maw={620}>
            Cara tercepat sapa kami: <strong>WhatsApp</strong>. Tim Bali balas dalam jam kerja. Mau lihat aplikasinya
            dulu? Buka demo — tidak perlu daftar.
          </Text>
        </Stack>

        <SimpleGrid cols={{ base: 1, md: 2 }} spacing="lg" mb="xl">
          {/* Primary CTA — green WhatsApp tile, biggest target */}
          <Anchor
            href={WA_LINK}
            target="_blank"
            rel="noopener noreferrer"
            className={`${classes.ctaTile} ${classes.ctaWhatsapp}`}
          >
            <Group gap="md" wrap="nowrap">
              <ThemeIcon size={56} radius="xl" color="green" variant="filled">
                <FaWhatsapp size={28} />
              </ThemeIcon>
              <div>
                <Text fw={700} fz="xl" mb={2}>
                  Chat WhatsApp
                </Text>
                <Text size="sm" className={classes.ctaSubtle}>
                  +62 812-3456-7890 · paling cepat dijawab
                </Text>
              </div>
            </Group>
            <Text className={classes.ctaArrow}>
              <LuArrowRight size={22} />
            </Text>
          </Anchor>

          {/* Secondary CTA — demo */}
          <DemoTile />
        </SimpleGrid>

        <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="md">
          <Paper p="md" radius="md" component="a" href="mailto:support@open-marina.id" className={classes.infoTile}>
            <Group gap="sm" wrap="nowrap">
              <ThemeIcon variant="light" color="laut" size="lg" radius="md">
                <LuMail size={18} />
              </ThemeIcon>
              <div>
                <Text size="xs" c="dimmed" tt="uppercase" fw={500}>
                  Email
                </Text>
                <Text fw={500} size="sm">
                  support@open-marina.id
                </Text>
              </div>
            </Group>
          </Paper>

          <Paper p="md" radius="md" className={classes.infoTile}>
            <Group gap="sm" wrap="nowrap">
              <ThemeIcon variant="light" color="laut" size="lg" radius="md">
                <LuMessageCircle size={18} />
              </ThemeIcon>
              <div>
                <Text size="xs" c="dimmed" tt="uppercase" fw={500}>
                  Jam Kerja
                </Text>
                <Text fw={500} size="sm">
                  Sen–Jum · 09:00–18:00 WITA
                </Text>
              </div>
            </Group>
          </Paper>

          <Paper p="md" radius="md" className={classes.infoTile}>
            <Group gap="sm" wrap="nowrap">
              <ThemeIcon variant="light" color="laut" size="lg" radius="md">
                <LuMapPin size={18} />
              </ThemeIcon>
              <div>
                <Text size="xs" c="dimmed" tt="uppercase" fw={500}>
                  Kantor
                </Text>
                <Text fw={500} size="sm">
                  Denpasar, Bali
                </Text>
              </div>
            </Group>
          </Paper>
        </SimpleGrid>
      </Container>
    </Box>
  )
}

function DemoTile() {
  const [pending, setPending] = useState(false)
  const config = useQuery({
    queryKey: ['demo', 'config'],
    queryFn: async () => {
      const res = await fetch('/api/demo/config')
      if (!res.ok) return { enabled: false }
      return (await res.json()) as { enabled: boolean }
    },
    retry: false,
    staleTime: 5 * 60_000,
  })

  // When demo is disabled, the second CTA falls back to "email kami"
  // which is one tap (mailto:) — still no form.
  if (!config.data?.enabled) {
    return (
      <Anchor
        href="mailto:support@open-marina.id?subject=Demo%20Request"
        className={`${classes.ctaTile} ${classes.ctaTeal}`}
      >
        <Group gap="md" wrap="nowrap">
          <ThemeIcon size={56} radius="xl" color="laut" variant="filled">
            <LuMail size={26} />
          </ThemeIcon>
          <div>
            <Text fw={700} fz="xl" mb={2}>
              Email Demo Request
            </Text>
            <Text size="sm" className={classes.ctaSubtle}>
              Tim kami akan reply 1×24 jam dengan link demo
            </Text>
          </div>
        </Group>
        <Text className={classes.ctaArrow}>
          <LuArrowRight size={22} />
        </Text>
      </Anchor>
    )
  }

  async function start() {
    setPending(true)
    try {
      const res = await fetch('/api/demo/start', { method: 'POST', credentials: 'include' })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const body = (await res.json()) as { redirectTo?: string }
      window.location.assign(body.redirectTo ?? '/dashboard')
    } catch (e) {
      setPending(false)
      console.error('[Demo] start failed', e)
    }
  }

  return (
    <Button onClick={start} loading={pending} className={`${classes.ctaTile} ${classes.ctaTeal}`} variant="unstyled">
      <Group gap="md" wrap="nowrap" w="100%">
        <ThemeIcon size={56} radius="xl" color="laut" variant="filled">
          <LuFlaskConical size={26} />
        </ThemeIcon>
        <div style={{ textAlign: 'left' }}>
          <Text fw={700} fz="xl" mb={2}>
            Coba Demo Live
          </Text>
          <Text size="sm" className={classes.ctaSubtle}>
            Login otomatis · 0 form · Akses penuh
          </Text>
        </div>
      </Group>
      <Text className={classes.ctaArrow}>
        <LuArrowRight size={22} />
      </Text>
    </Button>
  )
}
